import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import type { ReactNode, RefObject } from 'react';

import { minimapExpandedAtom } from '@/atoms/floatingWindow.atom';
import { useWalkToolbarVisible } from '@/components/AlignmentWalkToolbar';
import { FloatingWindow, type FloatingWindowHandleProps } from '@/components/ui/FloatingWindow';
import { useIsMobile } from '@/hooks/use-mobile';
import { WINDOW_BASE_Z } from '@/lib/floatingWindow';
import { cn } from '@/lib/utils';

/**
 * Spec §12.2's three adopters: the viewer's minimap, the alignment walk
 * toolbar and the top-right analysis column, each wrapped in the
 * `FloatingWindow` primitive so the user can move it.
 *
 * They live here rather than inline in `Viewer.tsx` for two reasons: `Viewer`
 * is already ~2 000 lines, and `Viewer` itself cannot be mounted in jsdom
 * (WebGL) — as separate components the wrappers are testable for real
 * (`__tests__/ViewerWindows.dom.test.tsx`) instead of grepped out of the
 * source. Each takes the canvas region as `boundsRef` (§12.1's `bounds`) and
 * its content as children; the default position, the drag, the clamp, the
 * z-order and the persistence are all the primitive's.
 *
 * The grips are deliberately quiet: the primitive supplies the 44 px touch
 * target, the neutral/hover tokens the viewer's `FloatingToolbar` buttons use
 * and the focus ring, so all this file adds is where the grip sits and a dot
 * glyph to say it is one.
 */

/** The one visual the grips share: a stubby bar in the handle's own ink. */
/**
 * The one drag affordance every window shares: the six-dot "grip" glyph
 * (two columns of three), the mark most people already read as "drag me"
 * from window managers and mobile sheets. Quiet by default (muted ink, no
 * chrome of its own), darker on hover, and the `title` says what it does.
 */
const GripGlyph = ({ className }: { className?: string }) => (
  <svg aria-hidden viewBox='0 0 10 16' width='10' height='16' className={cn('fill-current', className)}>
    <circle cx='2.5' cy='2.5' r='1.5' />
    <circle cx='7.5' cy='2.5' r='1.5' />
    <circle cx='2.5' cy='8' r='1.5' />
    <circle cx='7.5' cy='8' r='1.5' />
    <circle cx='2.5' cy='13.5' r='1.5' />
    <circle cx='7.5' cy='13.5' r='1.5' />
  </svg>
);

/** Shared grip look: muted ink, no background, darker ink on hover/focus. */
const GRIP_INK =
  'bg-transparent text-slate-400 hover:bg-transparent hover:text-slate-700 focus-visible:text-slate-700 dark:text-stone-500 dark:hover:bg-transparent dark:hover:text-stone-200 dark:focus-visible:text-stone-200';

interface ViewerWindowProps {
  /** The canvas region the window is confined to (`#canvas-container`). */
  boundsRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}

/**
 * The corner minimap (`MapView` in overlay mode).
 *
 * The grip takes the map's top-RIGHT corner — the one corner of the card that
 * is empty. It cannot go across the top: the map's own controls (top-left) and
 * its resize corner (bottom-right) only appear while the CARD is hovered, and
 * the grip is a sibling of the card rather than a child, so a grip lying over
 * the controls would make them vanish as the pointer approached them.
 *
 * It fades in with those controls — on hovering the window, which includes the
 * map — so a resting minimap looks exactly as it did. `focus-visible` brings it
 * back for keyboard users, who cannot hover.
 */
export const MinimapWindow = ({ boundsRef, children }: ViewerWindowProps) => {
  const { t } = useTranslation('project');
  const label = t('viewerWindows.moveMinimap');
  const hint = t('viewerWindows.dragHint');
  // Expanded, the map sizes its card to the whole canvas region, so the window
  // holds its anchor for the duration (ruling D57) — a region-sized card at a
  // dragged offset is the sliver the Expand control used to produce. The
  // stored offset is untouched and comes back on collapse.
  const expanded = useAtomValue(minimapExpandedAtom);
  return (
    <FloatingWindow
      id='minimap'
      boundsRef={boundsRef}
      label={label}
      atAnchor={expanded}
      // `group` so the grip can fade in on hovering the window, the way
      // `MapView`'s own controls fade in on hovering the card.
      className='group'
      handle={(p: FloatingWindowHandleProps) => (
        <button
          {...p}
          title={`${label}. ${hint}`}
          className={cn(
            p.className,
            GRIP_INK,
            'absolute top-0 right-0 z-40 h-11 w-11 rounded-none rounded-tr-md',
            // Touch has no hover, so the grip is always shown there.
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100',
          )}>
          <GripGlyph />
        </button>
      )}>
      {children}
    </FloatingWindow>
  );
};

/**
 * The alignment walk toolbar.
 *
 * The window is HIDDEN, never unmounted, while the toolbar would render
 * nothing (`useWalkToolbarVisible` — the bar's own `return null` predicate).
 * Two reasons it cannot simply unmount:
 * - the bar must stay mounted through the end of a walk: its clip-box restore
 *   is an effect that runs when `walk` goes null on a still-mounted bar, and
 *   unmounting it in that same commit would leave the viewer clipped for good;
 * - a lone grip on the canvas with nothing to drag is what the user reported.
 * So the window takes the primitive's `hidden` prop — inline
 * `visibility: hidden` + `pointer-events: none` + `aria-hidden` (an `invisible`
 * class would lose to the primitive's own inline `visibility`) — and the grip
 * drops out of the tab order.
 *
 * The grip lives INSIDE the toolbar's card, in the 20 px of left padding the
 * bar reserves for it (`pl-5` on its row), as a thin vertical bar: the card is
 * the window, and one quiet mark at its edge says it moves. No `relative`
 * here — the primitive's root is already `absolute` (a `relative` would
 * replace it under tailwind-merge and drop the window into normal flow).
 */
export const WalkToolbarWindow = ({ boundsRef, children }: ViewerWindowProps) => {
  const { t } = useTranslation('project');
  const visible = useWalkToolbarVisible();
  const label = t('viewerWindows.moveWalkToolbar');
  const hint = t('viewerWindows.dragHint');
  return (
    <FloatingWindow
      id='walkToolbar'
      boundsRef={boundsRef}
      label={label}
      hidden={!visible}
      handle={(p: FloatingWindowHandleProps) => (
        <button
          {...p}
          tabIndex={visible ? p.tabIndex : -1}
          title={`${label}. ${hint}`}
          className={cn(
            p.className,
            GRIP_INK,
            // Overlays the card's left padding; `min-w-0` overrides the
            // primitive's 44 px minimum width — the 44 px touch target is the
            // card's own height, which the grip spans (`inset-y-0`).
            'absolute inset-y-0 left-0 z-10 w-5 min-w-0 rounded-l-md rounded-r-none',
          )}>
          <GripGlyph />
        </button>
      )}>
      {children}
    </FloatingWindow>
  );
};

/**
 * The top-right analysis column (rainfall summaries, deviation colorbars and
 * volume panels, surface-difference panels).
 *
 * Two things this window owes the rest of the viewer:
 *
 * - **It must not eat canvas drags.** The window ROOT is `pointer-events-none`,
 *   so the empty space it spans — everything outside the grip and the column —
 *   stays canvas. The scroll container itself is `pointer-events-auto` (ruling
 *   D50): an `overflow-y-auto` box whose scrollbar cannot be grabbed is not a
 *   scrollable box. The trade that buys is that a wheel over the gaps BETWEEN
 *   panels now scrolls the column rather than dollying the camera — accepted,
 *   since those gaps are 8 px and the cursor is inside a panel stack.
 * - **It must never hang below the canvas** (ruling D44). Its height is
 *   `min(ANALYSIS_COLUMN_MAX_H, regionHeight − 2 × WINDOW_MARGIN_PX)` — the CSS
 *   twin of `defaultWindowSizes(bounds).analysisColumn.h`, so the slot the
 *   §12.2 defaults reserve and the box that actually renders agree. Opening one
 *   more panel scrolls the column instead of pushing its bottom under the
 *   station-volumes dock.
 */
export const AnalysisColumnWindow = ({
  boundsRef,
  children,
  hidden,
}: ViewerWindowProps & {
  /** The viewer's "hide analysis panels" toggle. Kept mounted so each panel's
   *  position and expanded/collapsed state survive the round trip. */
  hidden?: boolean;
}) => {
  const { t } = useTranslation('project');
  const label = t('viewerWindows.moveAnalysisColumn');
  const hint = t('viewerWindows.dragHint');
  const isNarrow = useIsMobile();

  // A phone is narrower than the 384 px column: as a movable window it covered
  // the whole canvas (and a desktop-dragged offset came back with it). There it
  // is a full-width strip across the top instead, at most 45 % of the canvas
  // tall and scrolling inside; the cards collapse to their headers, and the
  // Tools sheet's "hide analysis panels" still hides it.
  if (isNarrow) {
    return (
      <div
        data-testid='analysis-column-narrow'
        aria-hidden={hidden || undefined}
        style={{ zIndex: WINDOW_BASE_Z, visibility: hidden ? 'hidden' : 'visible' }}
        className='pointer-events-none absolute inset-x-2 top-2 flex max-h-[45%] flex-col'
      >
        <div
          data-testid='analysis-column'
          // `p-1`: the scroll box clips, and without room the cards' borders
          // and shadows were cut flush at its edges.
          className='pointer-events-auto flex min-h-0 w-full flex-col gap-2 overflow-y-auto overscroll-contain p-1'
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <FloatingWindow
      id='analysisColumn'
      boundsRef={boundsRef}
      label={label}
      hidden={hidden}
      // `max-h-[min(640px,calc(100%-1rem))]` is `ANALYSIS_COLUMN_MAX_H` and
      // `WINDOW_MARGIN_PX` from `lib/floatingWindow.ts` written as CSS; the
      // percentage resolves against the canvas region, which is this absolutely
      // positioned window's containing block. Change one, change the other.
      className='pointer-events-none flex max-h-[min(640px,calc(100%-1rem))] flex-col'
      handle={(p: FloatingWindowHandleProps) => (
        <button
          {...p}
          title={`${label}. ${hint}`}
          // 24 px visual bar; the window's clamp still keeps the top 44 px
          // reachable (D41), and this bar lies inside that band.
          style={{ minHeight: 24 }}
          className={cn(
            p.className,
            GRIP_INK,
            // A compact chrome-less handle above the panels, `self-start` so
            // the right edge — where the viewer's own action toolbar and clip
            // rail sit when `actionPosition === 'RIGHT'` — is never covered by
            // anything clickable.
            'pointer-events-auto mb-0.5 h-6 w-11 min-w-0 self-start rounded-md',
          )}>
          <GripGlyph className='rotate-90' />
        </button>
      )}>
      {/* One shared column for ALL top-right analysis panels: two separate
          `top-4 right-4` wrappers used to overlap, because absolute positioning
          has no awareness of siblings. `min-h-0` is what lets `overflow-y-auto`
          actually scroll inside the flex column above. */}
      {/* `px-3 py-2` is not decoration: `overflow-y-auto` forces `overflow-x`
          to compute to `auto` too, so the box CLIPS horizontally, and every
          panel in here is a card with a `shadow-lg`. Without room inside the
          clip box each card's shadow was sliced off flush at both edges (and
          the first and last card's at top and bottom), which is what made the
          column read as a set of cut-out rectangles rather than floating
          cards. The cards give up 24 px of width for it; widening the window
          instead would mean changing `WINDOW_DEFAULT_SIZES.analysisColumn.w`
          and every CSS twin and test that pins 384. */}
      <div
        data-testid='analysis-column'
        className='pointer-events-auto flex min-h-0 w-96 flex-col gap-2 overflow-y-auto px-3 py-2'>
        {children}
      </div>
    </FloatingWindow>
  );
};
